import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, Car, Wrench, Search, CreditCard } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function Footer() {
  const currentYear = 2026;
  const { t } = useLanguage();

  return (
    <footer className="bg-[#08070b] text-white mt-auto border-t border-white/10">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#ff3b00] rounded-full flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold">
                <span className="text-[#ff3b00]">VROUM</span>
              </span>
            </Link>
            <p className="text-white/60 text-sm mb-3">
              {t('footer.description')}
            </p>
            {/* 100% Greek badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-400/30 rounded-full mb-4">
              <span className="text-base">🇬🇷</span>
              <span className="text-[11px] font-bold text-blue-300 uppercase tracking-wider">
                {t('footer.madeIn')}
              </span>
            </div>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a 
                href="https://www.facebook.com/profile.php?id=61579690088251" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1877f2] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com/vroumgr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="mailto:info@vroum.gr"
                className="w-10 h-10 bg-[#ff3b00] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t('footer.quickAccess')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/search" className="text-white/60 hover:text-[#ff3b00] transition-colors flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  {t('footer.searchGarages')}
                </Link>
              </li>
              <li>
                <Link to="/obd" className="text-white/60 hover:text-[#ff3b00] transition-colors flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  {t('footer.obdCodes')}
                </Link>
              </li>
              <li>
                <Link to="/price-estimator" className="text-white/60 hover:text-[#ff3b00] transition-colors flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {t('footer.priceEstimate')}
                </Link>
              </li>
              <li>
                <Link to="/add-garage" className="text-white/60 hover:text-[#ff3b00] transition-colors flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  {t('footer.addGarage')}
                </Link>
              </li>
            </ul>
          </div>

          {/* For Businesses */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t('footer.forGarages')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/giati-na-diektimis" className="text-[#ff3b00] hover:text-orange-400 transition-colors font-semibold">
                  {t('footer.claimGarage')}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-white/60 hover:text-[#ff3b00] transition-colors">
                  {t('footer.plansAndPricing')}
                </Link>
              </li>
              <li>
                <Link to="/kataskevi-istoselidas-synergeia" className="text-amber-400 hover:text-amber-300 transition-colors font-semibold inline-flex items-center gap-1">
                  Φτιάξε site από 450€ <span className="text-xs">🔥</span>
                </Link>
              </li>
              <li>
                <a href="https://kaneprosfora.gr" target="_blank" rel="noopener" className="text-white/60 hover:text-[#ff3b00] transition-colors">
                  Εργαλείο προσφορών
                </a>
              </li>
              <li>
                <Link to="/register" className="text-white/60 hover:text-[#ff3b00] transition-colors">
                  {t('footer.garageSignup')}
                </Link>
              </li>
              <li>
                <Link to="/my-garage" className="text-white/60 hover:text-[#ff3b00] transition-colors">
                  {t('footer.dashboard')}
                </Link>
              </li>
              <li>
                <a href="mailto:info@vroum.gr" className="text-white/60 hover:text-[#ff3b00] transition-colors">
                  {t('footer.b2bContact')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-white/60">
                <Mail className="w-5 h-5 text-[#ff3b00]" />
                <a href="mailto:info@vroum.gr" className="hover:text-[#ff3b00] transition-colors">
                  info@vroum.gr
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/60">
                <MapPin className="w-5 h-5 text-[#ff3b00]" />
                <span>{t('footer.location')}</span>
              </li>
            </ul>
            
            {/* Trust Badges */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-white/40 mb-2">{t('footer.securePayments')}</p>
              <div className="flex items-center gap-2">
                <div className="bg-white/10 px-2 py-1 rounded text-xs">Stripe</div>
                <div className="bg-white/10 px-2 py-1 rounded text-xs">SSL</div>
                <div className="bg-white/10 px-2 py-1 rounded text-xs">GDPR</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* SEO Links - Cities & Services */}
        <div className="border-t border-white/10 mt-8 pt-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-sm font-semibold text-white/80 mb-3">{t('footer.popularCities')}</h4>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: 'Αθήνα', slug: 'athina' },
                  { name: 'Θεσσαλονίκη', slug: 'thessaloniki' },
                  { name: 'Πάτρα', slug: 'patra' },
                  { name: 'Ηράκλειο', slug: 'irakleio' },
                  { name: 'Λάρισα', slug: 'larisa' },
                  { name: 'Βόλος', slug: 'volos' },
                  { name: 'Χανιά', slug: 'chania' },
                  { name: 'Ρόδος', slug: 'rodos' },
                  { name: 'Λευκωσία', slug: 'lefkosia' },
                  { name: 'Λεμεσός', slug: 'lemesos' },
                  { name: 'Λάρνακα', slug: 'larnaka' },
                  { name: 'Πάφος', slug: 'pafos' },
                ].map((city) => (
                  <Link
                    key={city.slug}
                    to={`/synergeia/${city.slug}`}
                    className="text-xs text-white/40 hover:text-[#ff3b00] transition-colors"
                  >
                    {t('footer.garagesPrefix')} {city.name}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white/80 mb-3">{t('footer.servicesLabel')}</h4>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: 'Service', slug: 'service-aytokinitou' },
                  { name: 'Φρένα', slug: 'frena' },
                  { name: 'Ελαστικά', slug: 'elastika' },
                  { name: 'Διάγνωση', slug: 'diagnosi' },
                  { name: 'Φανοποιείο', slug: 'fanopoieio' },
                  { name: 'Κλιματισμός', slug: 'klimatismos' },
                  { name: 'Ηλεκτρολογικά', slug: 'ilektrologeio' },
                  { name: 'Αλλαγή Λαδιών', slug: 'allagi-ladion' },
                ].map((svc) => (
                  <Link
                    key={svc.slug}
                    to={`/synergeia/athina/${svc.slug}`}
                    className="text-xs text-white/40 hover:text-[#ff3b00] transition-colors"
                  >
                    {svc.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tools partner row — Calchy (tire-pressure database, contextual to the
         elastika service category). NOTE: no garage/advertiser links here — the
         marketplace stays visibly independent of any single workshop (incl. the
         founder's own DMS Tuned) so Territory Exchange bidders trust its neutrality. */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/30">Εργαλεία</span>
            <a
              href="https://calchy.com/tire-pressure"
              rel="noopener"
              className="text-xs text-[#ff3b00] hover:text-white transition-colors font-medium"
            >
              Calchy — Πιέσεις ελαστικών ανά μοντέλο
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">
              © {currentYear} <strong className="text-white/60">VROUM</strong> · {t('footer.copyright')} 🇬🇷 · {t('footer.copyrightHq')} · <a href="https://www.pamebrosta.gr/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Ιστοσελίδα για το συνεργείο σου</a>
            </p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy" className="text-white/40 hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms" className="text-white/40 hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
              <a 
                href="/sitemap.xml" 
                className="text-white/40 hover:text-white transition-colors"
                target="_blank"
              >
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
